import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { Card, CardContent } from '../ui/card';
import { Button } from '../ui/button';
import { Skeleton } from '../ui/skeleton';
import NgoOfTheMonth from '../NgoOfTheMonth';
import {
  MessageSquare,
  BookOpen,
  Calendar,
  ShoppingBag,
  ArrowRight,
  Users,
  Heart,
  Sparkles,
  PawPrint,
  TrendingUp,
  Globe,
  Building2,
  Handshake,
  MapPin,
  Crown,
  Mail
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

/**
 * Feature card data with links to actual pages
 * guestAccessible: true = viewable without login (shown with green indicator)
 */
const FEATURES = [
  {
    id: 'forums',
    icon: MessageSquare,
    title: 'Discussion Forums',
    description: 'Share tips, ask questions, and connect with fellow pet lovers in our vibrant community forums.',
    color: 'bg-primary/10 text-primary',
    link: '/forums',
    linkLabel: 'Browse Forums',
    guestAccessible: true
  },
  {
    id: 'catalog',
    icon: BookOpen,
    title: 'Pet Catalog',
    description: 'Build your digital pack. Document your pets with care guides and share with the community.',
    color: 'bg-secondary/10 text-secondary',
    link: '/my-pets',
    linkLabel: 'My Pets',
    guestAccessible: false
  },
  {
    id: 'events',
    icon: Calendar,
    title: 'Events & Meetups',
    description: 'Join local walks, training workshops, and playdates. Never miss a community event.',
    color: 'bg-amber-100 text-amber-700',
    link: '/events',
    linkLabel: 'View Events',
    guestAccessible: true
  },
  {
    id: 'marketplace',
    icon: ShoppingBag,
    title: 'Marketplace',
    description: 'Buy, sell, or trade pet supplies and accessories with trusted community members.',
    color: 'bg-emerald-100 text-emerald-700',
    link: '/marketplace',
    linkLabel: 'Shop Now',
    guestAccessible: false
  }
];

/**
 * Interactive Feature Card Component
 */
export const FeatureCard = ({ feature, index }) => {
  const navigate = useNavigate();
  const { user } = useAuth();
  
  const handleClick = () => {
    // Some pages require auth
    const authRequired = ['my-pets'].some(p => feature.link.includes(p));
    if (authRequired && !user) {
      navigate('/login', { state: { from: feature.link } });
    } else {
      navigate(feature.link);
    }
  };

  return (
    <Card 
      className={`card-interactive group cursor-pointer hover:shadow-lg transition-all duration-300 ${feature.guestAccessible ? 'ring-2 ring-emerald-300' : ''}`}
      onClick={handleClick}
      data-testid={`feature-card-${feature.id}`}
    >
      <CardContent className="p-6">
        <div className="flex items-start justify-between mb-4">
          <div className={`w-14 h-14 rounded-xl ${feature.color} flex items-center justify-center group-hover:scale-110 transition-transform`}>
            <feature.icon className="w-6 h-6" />
          </div>
          {feature.guestAccessible && (
            <div className="flex items-center gap-1 px-2 py-1 bg-emerald-50 text-emerald-700 rounded-full text-xs font-medium">
              <Globe className="w-3 h-3" />
              Open
            </div>
          )}
        </div>
        <h3 className="text-xl font-heading font-medium mb-2">{feature.title}</h3>
        <p className="text-muted-foreground text-sm leading-relaxed mb-4">{feature.description}</p>
        <div className="flex items-center text-sm font-medium text-primary opacity-0 group-hover:opacity-100 transition-opacity">
          {feature.linkLabel}
          <ArrowRight className="w-4 h-4 ml-1 group-hover:translate-x-1 transition-transform" />
        </div>
      </CardContent>
    </Card>
  );
};

/**
 * Feature Grid Section
 */
export const FeatureGrid = () => {
  return (
    <section className="section-padding bg-muted/50" data-testid="feature-grid-section">
      <div className="container mx-auto px-4">
        <div className="text-center mb-16">
          <h2 className="text-4xl md:text-5xl font-heading font-medium mb-4">
            Everything Your Dog Needs to <span className="italic text-secondary">Thrive</span>
          </h2>
          <p className="text-muted-foreground text-lg max-w-2xl mx-auto">
            From sharing training tips to organizing playdates, our club is building everything 
            a pet lover could ask for.
          </p>
        </div>

        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
          {FEATURES.map((feature, idx) => (
            <FeatureCard key={feature.id} feature={feature} index={idx} />
          ))}
        </div>
      </div>
    </section>
  );
};

/**
 * Live Stats Component - Fetches real data from API
 */
export const LiveStats = ({ variant = 'hero' }) => {
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchStats();
  }, []);

  const fetchStats = async () => {
    try {
      const response = await fetch(`${API_URL}/api/stats/public`);
      if (response.ok) {
        const data = await response.json();
        setStats(data);
      }
    } catch (error) {
      console.error('Error fetching stats:', error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="grid grid-cols-2 gap-8">
        {[1, 2, 3, 4].map((i) => (
          <div key={i} className="text-center">
            <Skeleton className="h-12 w-20 mx-auto mb-2" />
            <Skeleton className="h-4 w-16 mx-auto" />
          </div>
        ))}
      </div>
    );
  }

  const displayStats = stats?.hero_stats || [
    { value: '10+', label: 'Pet Lovers' },
    { value: '10+', label: 'Pets Registered' },
    { value: '5+', label: 'Events Hosted' },
    { value: '5+', label: 'Local Groups' }
  ];

  if (variant === 'hero') {
    return (
      <Card className="bg-white/10 backdrop-blur-md border-white/20">
        <CardContent className="p-8">
          <div className="grid grid-cols-2 gap-8">
            {displayStats.map((stat, idx) => (
              <div key={idx} className="text-center">
                <div className="stat-number text-white">{stat.value}</div>
                <div className="text-white/70 text-sm mt-1">{stat.label}</div>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>
    );
  }

  // Inline variant for other sections
  return (
    <div className="flex flex-wrap gap-6 justify-center">
      {displayStats.map((stat, idx) => (
        <div key={idx} className="text-center px-4">
          <div className="text-3xl font-bold text-primary">{stat.value}</div>
          <div className="text-sm text-muted-foreground">{stat.label}</div>
        </div>
      ))}
    </div>
  );
};

/**
 * Community Section Component
 */
export const CommunitySection = () => {
  const navigate = useNavigate();
  const { user } = useAuth();

  const benefits = [
    'Share training tips and learn from experienced owners',
    'Join local groups and attend pet meetups',
    'Get personalized advice for your dogs',
    'Build lasting friendships with fellow enthusiasts'
  ];

  return (
    <section className="section-padding" data-testid="community-section">
      <div className="container mx-auto px-4">
        <div className="grid lg:grid-cols-2 gap-12 items-center">
          <div className="relative">
            <img
              src="https://images.unsplash.com/photo-1601758228041-f3b2795255f1?w=800&q=80"
              alt="Pet community"
              className="rounded-2xl shadow-hover"
            />
            <div className="absolute -bottom-6 -right-6 bg-card rounded-xl p-4 shadow-lg border">
              <div className="flex items-center gap-3">
                <div className="flex -space-x-3">
                  {[1, 2, 3, 4].map((i) => (
                    <div key={i} className="w-10 h-10 rounded-full bg-primary/20 border-2 border-card flex items-center justify-center">
                      <Users className="w-4 h-4 text-primary" />
                    </div>
                  ))}
                </div>
                <div>
                  <div className="font-medium">Growing Daily</div>
                  <div className="text-xs text-muted-foreground">Active Members</div>
                </div>
              </div>
            </div>
          </div>

          <div>
            <span className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-secondary/10 text-secondary text-sm mb-6">
              <Heart className="w-4 h-4" />
              Join our pack
            </span>
            
            <h2 className="text-4xl md:text-5xl font-heading font-medium leading-tight mb-6">
              Grow Together with
              <br />
              <span className="italic">Like-minded</span> Pet Lovers
            </h2>
            
            <p className="text-muted-foreground text-lg mb-6">
              Connect with pet lovers who share your passion. Whether you're a first-time 
              pet parent or an experienced trainer, there's a place for you here.
            </p>

            <ul className="space-y-4 mb-8">
              {benefits.map((item, idx) => (
                <li key={idx} className="flex items-center gap-3">
                  <div className="w-6 h-6 rounded-full bg-primary/10 flex items-center justify-center">
                    <PawPrint className="w-3 h-3 text-primary" />
                  </div>
                  <span className="text-foreground">{item}</span>
                </li>
              ))}
            </ul>

            <Button 
              className="btn-primary"
              onClick={() => navigate(user ? '/groups' : '/register')}
              data-testid="community-cta"
            >
              {user ? 'Browse Groups' : 'Get Started Free'}
              <ArrowRight className="ml-2 w-4 h-4" />
            </Button>
          </div>
        </div>
      </div>
    </section>
  );
};

/**
 * CTA Section Component
 */
export const CTASection = () => {
  const navigate = useNavigate();
  const { user } = useAuth();

  if (user) return null;

  return (
    <section className="section-padding bg-primary text-primary-foreground" data-testid="cta-section">
      <div className="container mx-auto px-4 text-center">
        <h2 className="text-4xl md:text-5xl font-heading font-medium mb-4">
          Ready to Join the Pack?
        </h2>
        <p className="text-primary-foreground/80 text-lg max-w-xl mx-auto mb-8">
          Join our thriving community today and take your pet journey to the next level.
        </p>
        
        <Button 
          className="bg-amber-accent text-bark-900 hover:bg-amber-accent/90 h-12 px-10 rounded-full text-lg"
          onClick={() => navigate('/register')}
          data-testid="footer-cta"
        >
          Create Free Account
        </Button>
      </div>
    </section>
  );
};

/**
 * Why Join Section - Membership benefits for guests
 */
export const WhyJoinSection = () => {
  const navigate = useNavigate();

  const benefits = [
    {
      icon: Users,
      title: 'Connect with Pet Lovers',
      description: 'Join a community of passionate pet owners who share your love for animals.'
    },
    {
      icon: Calendar,
      title: 'Local Events & Meetups',
      description: 'Find playdates, training workshops, and community events near you.'
    },
    {
      icon: Heart,
      title: 'Health & Care Resources',
      description: 'Access AI health tools, medical record tracking, and expert advice.'
    },
    {
      icon: ShoppingBag,
      title: 'Member Marketplace',
      description: 'Buy, sell, and trade pet supplies with trusted community members.'
    },
    {
      icon: MessageSquare,
      title: 'Discussion Forums',
      description: 'Get answers to your questions and share your pet expertise.'
    },
    {
      icon: PawPrint,
      title: 'Pet Profiles & Family Trees',
      description: 'Create detailed profiles for your pets and track their lineage.'
    }
  ];

  return (
    <section className="section-padding bg-gradient-to-b from-muted/30 to-background" data-testid="why-join-section">
      <div className="container mx-auto px-4">
        <div className="text-center mb-12">
          <span className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-primary/10 text-primary text-sm mb-4">
            <Sparkles className="w-4 h-4" />
            Member Benefits
          </span>
          <h2 className="text-4xl md:text-5xl font-heading font-medium mb-4">
            Why Join <span className="italic text-secondary">AADC</span>?
          </h2>
          <p className="text-muted-foreground text-lg max-w-2xl mx-auto">
            From connecting with fellow pet lovers to accessing exclusive tools and resources, 
            here's what you get as a member.
          </p>
        </div>

        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6 mb-10">
          {benefits.map((benefit, idx) => (
            <Card key={idx} className="bg-card hover:shadow-lg transition-shadow">
              <CardContent className="p-6">
                <div className="w-12 h-12 rounded-xl bg-primary/10 flex items-center justify-center mb-4">
                  <benefit.icon className="w-6 h-6 text-primary" />
                </div>
                <h3 className="font-heading font-medium text-lg mb-2">{benefit.title}</h3>
                <p className="text-muted-foreground text-sm">{benefit.description}</p>
              </CardContent>
            </Card>
          ))}
        </div>

        <div className="text-center">
          <Button 
            size="lg"
            onClick={() => navigate('/register')}
            className="bg-primary hover:bg-primary/90"
            data-testid="why-join-cta"
          >
            Get Started Free
            <ArrowRight className="ml-2 w-4 h-4" />
          </Button>
          <p className="text-sm text-muted-foreground mt-3">
            No credit card required • Cancel anytime
          </p>
        </div>
      </div>
    </section>
  );
};

/**
 * Your Club Section - Community ownership messaging
 */
export const YourClubSection = () => {
  return (
    <section className="section-padding bg-gradient-to-br from-amber-50 to-orange-50" data-testid="your-club-section">
      <div className="container mx-auto px-4">
        <div className="max-w-4xl mx-auto text-center">
          <span className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-amber-100 text-amber-800 text-sm mb-6">
            <Heart className="w-4 h-4" />
            Community First
          </span>
          
          <h2 className="text-4xl md:text-5xl font-heading font-medium mb-6">
            This Is <span className="italic text-primary">Your</span> Club
          </h2>
          
          <p className="text-xl text-muted-foreground mb-8 leading-relaxed">
            We're not a faceless corporation chasing profits. We're building something different — 
            a club owned by its members, run for the benefit of pets and the people who love them. 
            Every decision we make puts <strong>you and your pets first</strong>.
          </p>
          
          <div className="grid md:grid-cols-3 gap-6 mt-10">
            <div className="bg-white/70 rounded-xl p-6 text-left">
              <div className="w-10 h-10 rounded-lg bg-amber-100 flex items-center justify-center mb-4">
                <Users className="w-5 h-5 text-amber-700" />
              </div>
              <h3 className="font-semibold mb-2">Member-Driven</h3>
              <p className="text-sm text-muted-foreground">
                Our roadmap is shaped by what members want. You vote on features, suggest improvements, and help guide our direction.
              </p>
            </div>
            <div className="bg-white/70 rounded-xl p-6 text-left">
              <div className="w-10 h-10 rounded-lg bg-amber-100 flex items-center justify-center mb-4">
                <Heart className="w-5 h-5 text-amber-700" />
              </div>
              <h3 className="font-semibold mb-2">Mission First</h3>
              <p className="text-sm text-muted-foreground">
                We exist to improve the lives of pets and strengthen the bond between pets and their families. That's our north star.
              </p>
            </div>
            <div className="bg-white/70 rounded-xl p-6 text-left">
              <div className="w-10 h-10 rounded-lg bg-amber-100 flex items-center justify-center mb-4">
                <Globe className="w-5 h-5 text-amber-700" />
              </div>
              <h3 className="font-semibold mb-2">Transparent</h3>
              <p className="text-sm text-muted-foreground">
                We share our plans openly, welcome feedback, and believe the best communities are built together.
              </p>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

/**
 * Partner With Us Section - Sponsors and partnerships
 */
export const PartnerSection = () => {
  const navigate = useNavigate();

  const partnerTypes = [
    {
      icon: Crown,
      title: 'Charter Sponsors',
      description: 'Become a founding sponsor and shape the future of pet communities. Site-wide visibility and recognition as a charter partner.',
      level: 'Site-Wide',
      color: 'bg-purple-100 text-purple-700'
    },
    {
      icon: MapPin,
      title: 'Regional Partners',
      description: 'Support pet communities in your region. Perfect for businesses serving specific states, cities, or metro areas.',
      level: 'Regional',
      color: 'bg-blue-100 text-blue-700'
    },
    {
      icon: Building2,
      title: 'Local Champions',
      description: 'Connect with pet owners in your community. Ideal for local vets, groomers, trainers, and pet shops.',
      level: 'Local',
      color: 'bg-green-100 text-green-700'
    },
    {
      icon: Handshake,
      title: 'Vendor Partnerships',
      description: 'Offer exclusive deals to our members. From pet food to insurance, reach engaged pet owners who trust our community.',
      level: 'Vendor',
      color: 'bg-amber-100 text-amber-700'
    },
    {
      icon: Heart,
      title: 'NGO & Rescue Partners',
      description: 'Shelters, rescues, and animal welfare organizations — partner with us to find forever homes and support your mission.',
      level: 'Non-Profit',
      color: 'bg-rose-100 text-rose-700'
    }
  ];

  return (
    <section className="section-padding" data-testid="partner-section">
      <div className="container mx-auto px-4">
        <div className="text-center mb-12">
          <span className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-primary/10 text-primary text-sm mb-4">
            <Handshake className="w-4 h-4" />
            Grow With Us
          </span>
          <h2 className="text-4xl md:text-5xl font-heading font-medium mb-4">
            Partner With <span className="italic text-secondary">AADC</span>
          </h2>
          <p className="text-muted-foreground text-lg max-w-2xl mx-auto">
            Join us in building something meaningful. Whether you're a business, NGO, or passionate 
            individual, there's a way to partner and make a difference.
          </p>
        </div>

        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6 mb-10">
          {partnerTypes.slice(0, 3).map((partner, idx) => (
            <Card key={idx} className="hover:shadow-lg transition-all hover:-translate-y-1">
              <CardContent className="p-6">
                <div className="flex items-center justify-between mb-4">
                  <div className="w-12 h-12 rounded-xl bg-primary/10 flex items-center justify-center">
                    <partner.icon className="w-6 h-6 text-primary" />
                  </div>
                  <span className={`px-3 py-1 rounded-full text-xs font-medium ${partner.color}`}>
                    {partner.level}
                  </span>
                </div>
                <h3 className="font-heading font-medium text-lg mb-2">{partner.title}</h3>
                <p className="text-muted-foreground text-sm">{partner.description}</p>
              </CardContent>
            </Card>
          ))}
        </div>

        <div className="grid md:grid-cols-2 gap-6 max-w-2xl mx-auto mb-10">
          {partnerTypes.slice(3).map((partner, idx) => (
            <Card key={idx} className="hover:shadow-lg transition-all hover:-translate-y-1">
              <CardContent className="p-6">
                <div className="flex items-center justify-between mb-4">
                  <div className="w-12 h-12 rounded-xl bg-primary/10 flex items-center justify-center">
                    <partner.icon className="w-6 h-6 text-primary" />
                  </div>
                  <span className={`px-3 py-1 rounded-full text-xs font-medium ${partner.color}`}>
                    {partner.level}
                  </span>
                </div>
                <h3 className="font-heading font-medium text-lg mb-2">{partner.title}</h3>
                <p className="text-muted-foreground text-sm">{partner.description}</p>
              </CardContent>
            </Card>
          ))}
        </div>

        <div className="text-center bg-gradient-to-r from-primary/5 to-secondary/5 rounded-2xl p-8">
          <h3 className="text-xl font-semibold mb-3">Interested in Partnering?</h3>
          <p className="text-muted-foreground mb-6 max-w-xl mx-auto">
            We'd love to hear from you. Reach out to discuss how we can work together to 
            support pet communities.
          </p>
          <div className="flex flex-wrap justify-center gap-4">
            <Button 
              onClick={() => navigate('/contact?type=sponsor')}
              className="gap-2"
            >
              <Crown className="w-4 h-4" />
              Become a Sponsor
            </Button>
            <Button 
              variant="outline"
              onClick={() => navigate('/contact?type=partner')}
              className="gap-2"
            >
              <Handshake className="w-4 h-4" />
              Partner With Us
            </Button>
            <Button 
              variant="outline"
              onClick={() => navigate('/contact?type=ngo')}
              className="gap-2"
            >
              <Heart className="w-4 h-4" />
              NGO Partnership
            </Button>
          </div>
        </div>
      </div>
    </section>
  );
};

/**
 * Recent Activity Feed Component
 */
export const RecentActivityFeed = () => {
  const [activity, setActivity] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchActivity();
  }, []);

  const fetchActivity = async () => {
    try {
      const response = await fetch(`${API_URL}/api/stats/activity`);
      if (response.ok) {
        const data = await response.json();
        setActivity(data);
      }
    } catch (error) {
      console.error('Error fetching activity:', error);
    } finally {
      setLoading(false);
    }
  };

  if (loading || !activity?.recent_activity?.length) return null;

  return (
    <div className="bg-muted/30 rounded-lg p-4">
      <h4 className="text-sm font-medium mb-3 flex items-center gap-2">
        <TrendingUp className="w-4 h-4 text-primary" />
        Recent Activity
      </h4>
      <ul className="space-y-2">
        {activity.recent_activity.map((item, idx) => (
          <li key={idx} className="text-sm text-muted-foreground">
            {item.text}
          </li>
        ))}
      </ul>
    </div>
  );
};

/**
 * Blog Preview Section - Shows latest blog posts on the landing page
 */
export const BlogPreviewSection = () => {
  const navigate = useNavigate();
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchPosts();
  }, []);

  const fetchPosts = async () => {
    try {
      const response = await fetch(`${API_URL}/api/posts?type=blog&limit=3`);
      if (response.ok) {
        const data = await response.json();
        setPosts(data);
      }
    } catch (error) {
      console.error('Error fetching blog posts:', error);
      // Use placeholder data
      setPosts([
        {
          id: '1',
          title: '10 Tips for New Dog Owners',
          excerpt: 'Essential advice for first-time pet parents...',
          image_url: 'https://images.unsplash.com/photo-1587300003388-59208cc962cb?w=400',
          category: 'tips',
          read_time: 5
        },
        {
          id: '2',
          title: 'Understanding Pet Nutrition',
          excerpt: 'What every pet owner should know about feeding...',
          image_url: 'https://images.unsplash.com/photo-1548199973-03cce0bbc87b?w=400',
          category: 'health',
          read_time: 7
        },
        {
          id: '3',
          title: 'Community Spotlight: Rescue Stories',
          excerpt: 'Heartwarming adoption stories from our members...',
          image_url: 'https://images.unsplash.com/photo-1601758228041-f3b2795255f1?w=400',
          category: 'community',
          read_time: 4
        }
      ]);
    } finally {
      setLoading(false);
    }
  };

  return (
    <section className="section-padding bg-muted/30" data-testid="blog-preview-section">
      <div className="container mx-auto px-4">
        <div className="flex items-center justify-between mb-8">
          <div>
            <h2 className="text-3xl font-heading font-medium mb-2">
              From Our Blog
            </h2>
            <p className="text-muted-foreground">
              Tips, news, and stories from the pet community
            </p>
          </div>
          <Button 
            variant="outline"
            onClick={() => navigate('/blog')}
            data-testid="view-all-blog-btn"
          >
            View All Posts
            <ArrowRight className="w-4 h-4 ml-2" />
          </Button>
        </div>

        {loading ? (
          <div className="grid md:grid-cols-3 gap-6">
            {[1, 2, 3].map((i) => (
              <Skeleton key={i} className="h-64 rounded-xl" />
            ))}
          </div>
        ) : (
          <div className="grid md:grid-cols-3 gap-6">
            {posts.map((post) => (
              <Card 
                key={post.id}
                className="overflow-hidden card-interactive cursor-pointer"
                onClick={() => navigate(`/blog/${post.id}`)}
              >
                <div className="h-40 relative">
                  {post.image_url ? (
                    <img
                      src={post.image_url}
                      alt={post.title}
                      className="w-full h-full object-cover"
                    />
                  ) : (
                    <div className="w-full h-full bg-gradient-to-br from-primary/20 to-secondary/20" />
                  )}
                  <span className="absolute top-3 left-3 px-2 py-1 bg-white/90 rounded text-xs font-medium">
                    {post.category}
                  </span>
                </div>
                <CardContent className="p-4">
                  <h3 className="font-medium mb-2 line-clamp-2">{post.title}</h3>
                  <p className="text-sm text-muted-foreground line-clamp-2 mb-2">
                    {post.excerpt}
                  </p>
                  <span className="text-xs text-muted-foreground">
                    {post.read_time} min read
                  </span>
                </CardContent>
              </Card>
            ))}
          </div>
        )}
      </div>
    </section>
  );
};

export default FeatureGrid;

/**
 * Adoption Section - Petfinder & Adopt-a-Pet integration
 * Links to adoption resources and showcases partnership
 */
export const AdoptionSection = () => {
  const navigate = useNavigate();

  const adoptionPartners = [
    {
      name: 'Petfinder',
      description: 'Search millions of adoptable pets from shelters nationwide',
      url: 'https://www.petfinder.com/',
      logo: 'https://www.petfinder.com/sites/all/themes/flavor/images/logo-white.svg',
      bgColor: 'bg-[#6a4c93]',
      stats: '11,000+ shelters'
    },
    {
      name: 'Adopt-a-Pet',
      description: 'North America\'s largest non-profit pet adoption website',
      url: 'https://www.adoptapet.com/',
      logo: null,
      bgColor: 'bg-[#ff6b35]',
      stats: '21,000+ rescues'
    },
    {
      name: 'Best Friends',
      description: 'Working to end killing in shelters by 2025',
      url: 'https://bestfriends.org/',
      logo: null,
      bgColor: 'bg-[#00a651]',
      stats: 'No-kill mission'
    }
  ];

  return (
    <section className="section-padding bg-gradient-to-b from-background to-rose-50/30" data-testid="adoption-section">
      <div className="container mx-auto px-4">
        <div className="text-center mb-12">
          <span className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-rose-100 text-rose-700 text-sm mb-4">
            <Heart className="w-4 h-4" />
            Adopt, Don't Shop
          </span>
          <h2 className="text-4xl md:text-5xl font-heading font-medium mb-4">
            Find Your <span className="italic text-rose-600">Forever Friend</span>
          </h2>
          <p className="text-muted-foreground text-lg max-w-2xl mx-auto">
            Millions of pets are waiting for loving homes. We are partnering with leading 
            adoption organizations to help you find your perfect match.
          </p>
        </div>

        {/* NGO of the Month Spotlight */}
        <div className="mb-10">
          <NgoOfTheMonth className="max-w-2xl mx-auto" />
        </div>

        <div className="grid md:grid-cols-3 gap-6 mb-10">
          {adoptionPartners.map((partner, idx) => (
            <a 
              key={idx}
              href={partner.url}
              target="_blank"
              rel="noopener noreferrer"
              className="group"
            >
              <Card className="h-full overflow-hidden hover:shadow-lg transition-all hover:-translate-y-1">
                <div className={`${partner.bgColor} p-6 text-white`}>
                  <div className="flex items-center justify-between mb-4">
                    <h3 className="text-xl font-heading font-medium">{partner.name}</h3>
                    <ArrowRight className="w-5 h-5 opacity-0 group-hover:opacity-100 transition-opacity" />
                  </div>
                  <p className="text-white/80 text-sm mb-3">{partner.description}</p>
                  <span className="inline-block px-3 py-1 bg-white/20 rounded-full text-xs">
                    {partner.stats}
                  </span>
                </div>
              </Card>
            </a>
          ))}
        </div>

        <div className="bg-rose-50 rounded-2xl p-8 text-center">
          <h3 className="text-2xl font-heading font-medium mb-4">
            Are You a Rescue or Shelter?
          </h3>
          <p className="text-muted-foreground mb-6 max-w-xl mx-auto">
            NGOs and rescue organizations can create a club on our platform for FREE.
            Share your adoptable animals, connect with potential adopters, and grow your community.
          </p>
          <Button 
            variant="outline"
            className="border-rose-300 text-rose-700 hover:bg-rose-100"
            onClick={() => navigate('/contact')}
          >
            Partner With Us
            <ArrowRight className="w-4 h-4 ml-2" />
          </Button>
        </div>
      </div>
    </section>
  );
};
